import * as types from 'src/store/mutation-types'
import { Notify } from 'quasar'
import { QuoteClient } from 'src/api/Quotes'
import { createErrorFromResponse } from 'src/utils/error'
import i18n from 'src/i18n/i18n'

// initial state
// shape: [{ id, quantity }]
const state = {
  quotes: [],
  quotesMeta: null,
  quotesLoaded: false
}

// getters
const getters = {
  quotes: state => state.quotes,
  quotesMeta: state => state.quotesMeta,
  quotesLoaded: state => state.quotesLoaded
}

const paginationDefault = {
  page: 1,
  rowsPerPage: 15,
  descending: true,
  sortBy: 'created_at',
  additionalIncludes: []
}

const actions = {
  listQuotes ({ commit, state }, { pagination } = {}) {
    pagination = { ...paginationDefault, ...(pagination || {}) }

    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']
    commit(types.SET_QUOTES_LOADED, false)

    return new QuoteClient()
      .include(['customer', ...pagination.additionalIncludes])
      .filter(pagination.filterBy || [])
      .sort(sort)
      .limit(pagination.rowsPerPage)
      .page(pagination.page)
      .list()
      .then(response => {
        commit(types.SET_QUOTES, response.data.data)
        commit(types.SET_QUOTES_META, response.data.meta)
        commit(types.SET_QUOTES_LOADED, true)
      })
      .then(() => Promise.resolve({ quotes: state.quotes, meta: state.quotesMeta }))
  },

  fetchNextQuotesChunk ({ commit, state }, { done, pagination = {}, type = [] }) {
    if (!state.quotesLoaded) {
      return done()
    } else if (!state.quotesMeta) {
      return done(true)
    }

    pagination = { ...paginationDefault, ...(pagination || {}) }
    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']

    let currentPage = state.quotesMeta.pagination.current_page
    let totalPages = state.quotesMeta.pagination.total_pages

    // Check if there is still a chunk to fetch available.
    if (currentPage < totalPages) {
      return new QuoteClient()
        .include(['customer', ...pagination.additionalIncludes])
        .filter(pagination.filterBy || [])
        .sort(sort)
        .limit(pagination.rowsPerPage)
        .page(currentPage + 1)
        .list()
        .then(response => {
          let newQuotes = state.quotes.concat(response.data.data)

          commit(types.SET_QUOTES, newQuotes)
          commit(types.SET_QUOTES_META, response.data.meta)

          // Indicate that data has been loaded successfully.
          done()
        })
    } else {
      // No more data available to fetch.
      // Passing true to the callback function tells the infinite scroll component to stop loading.
      done(true)
    }
  },

  searchQuotes ({ commit, state }, { pagination, filter }) {
    commit(types.SET_QUOTES_LOADED, false)

    return new QuoteClient()
      .include(['customer'])
      .search(filter)
      .then(response => {
        commit(types.SET_QUOTES, response.data.data)
        commit(types.SET_QUOTES_META, {
          pagination: {
            count: response.data.data.length,
            current_page: pagination.page,
            per_page: pagination.rowsPerPage,
            total: response.data.data.length,
            total_pages: 1
          }
        })
        commit(types.SET_QUOTES_LOADED, true)
      })
      .then(() => Promise.resolve({ quotes: state.quotes, meta: state.quotesMeta }))
  },

  deleteQuotes ({ commit, state }, quotes) {
    let ids = quotes.map(quote => quote.id)

    return new QuoteClient()
      .destroy(ids)
      .then(() => quotes.forEach(quote => commit(types.REMOVE_QUOTE, quote)))
      .then(() => commit(types.SET_QUOTES_META, {
        pagination: {
          ...state.quotesMeta.pagination,
          total: state.quotesMeta.pagination.total - quotes.length
        }
      }))
      .then(() => Notify.create({ message: i18n.tc('quote_deleted', 2), type: 'positive' }))
      .then(() => Promise.resolve({ quote: state.quotes, meta: state.quotesMeta }))
      .catch(createErrorFromResponse)
  },

  setQuoteStatus ({ commit }, { quoteId, status }) {
    return new QuoteClient()
      .include(['customer'])
      .setStatus(quoteId, { 'status_code': status })
      .then(response => {
        commit(types.UPDATE_QUOTE, response.data.data)
      })
  },

  refreshQuote ({ commit, state }, { quoteId, additionalIncludes = [] }) {
    new QuoteClient()
      .include(['customer', ...additionalIncludes])
      .get(quoteId)
      .then(response => {
        const quote = response.data.data[0]

        commit(types.UPDATE_QUOTE, quote)
      })
  },

  commitQuote ({ commit }, quoteId) {
    return new QuoteClient()
      .include(['customer'])
      .commit(quoteId)
      .then(response => {
        commit(types.UPDATE_QUOTE, response.data.data)
        return response.data.data
      })
  }
}

// mutations
const mutations = {
  [types.SET_QUOTES] (state, quotes) {
    state.quotes = quotes
  },

  [types.SET_QUOTES_META] (state, quotesMeta) {
    state.quotesMeta = quotesMeta
  },

  [types.SET_QUOTES_LOADED] (state, loaded) {
    state.quotesLoaded = loaded
  },

  [types.REMOVE_QUOTE] (state, quote) {
    const toDelete = state.quotes.find(stateQuote => stateQuote.id === quote.id)
    const index = state.quotes.indexOf(toDelete)

    if (index !== -1) {
      state.quotes.splice(index, 1)
    }
  },

  [types.UPDATE_QUOTE] (state, updateQuote) {
    let quoteIndex = state.quotes.findIndex(quote => quote.id === updateQuote.id)
    state.quotes.splice(quoteIndex, 1, updateQuote)
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
